import { useState, useCallback } from "react";
import _ from "lodash";
import { mapGradingPeriodsFromBackend } from "../modules/displaySettingsModalUtils";

/**
 * useGradingPeriodsMappingState
 *
 * Manages local draft state for grading period mappings across all grades.
 * Separate from the display settings `updates` map — uses a different mutation on Apply.
 *
 * Draft shape: gradingPeriodDrafts[gradeMapId] = [...]
 * Each entry is the desired final state (not a diff).
 * The diff (added/removed) is computed at Apply time by buildAllPayloads.
 * `added` acts as the desired final mapping state for periods still present
 * in the list, including `isEnabled: false` for unchecked items.
 */
export const useGradingPeriodsMappingState = ({ gradeMapCache }) => {
  const [gradingPeriodDrafts, setGradingPeriodDrafts] = useState({});

  // ---------- HELPERS ----------

  const getBackendPeriods = useCallback(
    gradeMapId => {
      const gradeMap = gradeMapCache.getGradeMap(gradeMapId);
      return mapGradingPeriodsFromBackend(
        _.get(gradeMap, "gradingPeriods", [])
      );
    },
    [gradeMapCache]
  );

  const getCurrentPeriods = useCallback(
    gradeMapId =>
      _.get(gradingPeriodDrafts, gradeMapId, null) ||
      getBackendPeriods(gradeMapId),
    [gradingPeriodDrafts, getBackendPeriods]
  );

  // ---------- HANDLERS ----------

  const handlePeriodSelectionChange = useCallback(
    ({ gradeMapId, currentPeriods }) => {
      setGradingPeriodDrafts(prev => {
        if (_.isEqual(_.get(prev, gradeMapId), currentPeriods)) return prev;
        return { ...prev, [gradeMapId]: currentPeriods };
      });
    },
    []
  );

  const handlePeriodToggle = useCallback(
    ({ gradeMapId, gradingPeriodId, isEnabled }) => {
      setGradingPeriodDrafts(prev => {
        const existing =
          _.get(prev, gradeMapId, null) || getBackendPeriods(gradeMapId);
        const updated = _.map(existing, p =>
          p.gradingPeriodId === gradingPeriodId ? { ...p, isEnabled } : p
        );
        if (_.isEqual(_.get(prev, gradeMapId), updated)) return prev;
        return { ...prev, [gradeMapId]: updated };
      });
    },
    [getBackendPeriods]
  );

  const handlePeriodReorder = useCallback(
    ({ gradeMapId, reorderedPeriods }) => {
      setGradingPeriodDrafts(prev => {
        const withSequence = _.map(reorderedPeriods, (p, index) => ({
          ...p,
          displaySequence: index + 1,
        }));
        if (_.isEqual(_.get(prev, gradeMapId), withSequence)) return prev;
        return { ...prev, [gradeMapId]: withSequence };
      });
    },
    []
  );

  // Reset on a grade: keep the current GP selection (draft if present,
  // else backend mapping), force every period to isEnabled: true.
  // Intentionally does NOT re-add periods the user has unchecked from the
  // grade — reset is about restoring enable/order defaults, not reverting
  // the user's selection.
  const resetGrade = useCallback(
    gradeMapId => {
      setGradingPeriodDrafts(prev => {
        const base =
          _.get(prev, gradeMapId, null) || getBackendPeriods(gradeMapId);
        const reset = _.map(base, p => ({ ...p, isEnabled: true }));
        return { ...prev, [gradeMapId]: reset };
      });
    },
    [getBackendPeriods]
  );

  // ---------- PAYLOAD BUILDER ----------

  const buildMappingPayload = useCallback(
    ({ gradeMapId }) => {
      const backendPeriods = getBackendPeriods(gradeMapId);
      const currentPeriods =
        _.get(gradingPeriodDrafts, gradeMapId, null) || backendPeriods;

      const backendIds = new Set(_.map(backendPeriods, "gradingPeriodId"));

      const added = _.map(currentPeriods, p => ({
        studentGradeMapId: gradeMapId,
        gradingPeriodId: p.gradingPeriodId,
        displaySequence: p.displaySequence,
        isEnabled: p.isEnabled,
      }));

      const currentIds = new Set(_.map(currentPeriods, "gradingPeriodId"));
      const removedIds = _.filter(
        Array.from(backendIds),
        id => !currentIds.has(id)
      );
      const removed = _.map(removedIds, gradingPeriodId => ({
        studentGradeMapId: gradeMapId,
        gradingPeriodId,
      }));

      return { added, removed };
    },
    [getBackendPeriods, gradingPeriodDrafts]
  );

  const buildAllPayloads = useCallback(() => {
    const touchedGradeMapIds = _.keys(gradingPeriodDrafts);
    const allAdded = [];
    const allRemoved = [];
    _.forEach(touchedGradeMapIds, gradeMapId => {
      const { added, removed } = buildMappingPayload({ gradeMapId });
      allAdded.push(...added);
      allRemoved.push(...removed);
    });
    return { added: allAdded, removed: allRemoved };
  }, [gradingPeriodDrafts, buildMappingPayload]);

  const removeDeletedPeriodsFromDrafts = useCallback(deletedGpIds => {
    if (_.isEmpty(deletedGpIds)) return;
    const deletedSet = new Set(_.map(deletedGpIds, String));
    setGradingPeriodDrafts(prev => {
      const next = {};
      let changed = false;
      _.forEach(prev, (periods, gradeMapId) => {
        const filtered = _.filter(
          periods,
          p => !deletedSet.has(String(p.gradingPeriodId))
        );
        if (filtered.length !== periods.length) changed = true;
        next[gradeMapId] = filtered;
      });
      return changed ? next : prev;
    });
  }, []);

  return {
    gradingPeriodDrafts,
    getCurrentPeriods,
    handlePeriodSelectionChange,
    handlePeriodToggle,
    handlePeriodReorder,
    buildAllPayloads,
    resetGrade,
    removeDeletedPeriodsFromDrafts,
  };
};
